/**
 * InlineDdlSecurityBody — the embeddable content (parked-statement scan +
 * `SecurityEditFormBody`), consumed directly by the Compact DDL editor's
 * `compactDdlBodyArm`. The arm dispatches to `InlineDdlGrantBody` instead
 * whenever `ddlKind` is `'grant'` or `'revoke'` — both share the `security`
 * objectKind.
 *
 * Mode is driven by the parked statement's leading keyword (CREATE → create,
 * ALTER → alter); the form is seeded with the parsed kind + name + mysql
 * host.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice F1.
 */
import { useMemo } from 'react';

import type { VqeDdlEditContext } from '../edit/editContext';
import { SecurityEditFormBody } from '../render/common/SecurityEditFormBody';
import {
  securityValuesFromParsed,
  type SecurityFormValues,
} from '../render/common/index';
import {
  parseParkedSecurityStatement,
  type ParsedParkedSecurity,
} from './parseParkedSecurityStatement';

export interface InlineDdlSecurityBodyProps {
  readonly editContext: VqeDdlEditContext;
  /** Test seam — overrides the parsed parked-security statement. */
  readonly parsedOverride?: ParsedParkedSecurity | null;
  /** Invoked after a successful commit or cancel, so an embedding popover can close. */
  readonly onDone?: () => void;
  /**
   * Cancel-only escape, when the host wants Cancel and commit to mean
   * DIFFERENT things. Falls back to `onDone` when omitted, so every existing
   * caller keeps the old "commit or cancel both call `onDone`" behaviour.
   * The compact popover passes it to close on Cancel WITHOUT closing on a
   * successful commit (the commit funnels through the app-shell
   * PreviewDialog, which needs this body’s context to stay mounted).
   */
  readonly onCancel?: () => void;
}

export function InlineDdlSecurityBody({
  editContext,
  parsedOverride,
  onDone,
  onCancel,
}: InlineDdlSecurityBodyProps) {
  const parsed = useMemo<ParsedParkedSecurity | null>(() => {
    if (parsedOverride !== undefined) return parsedOverride;
    try {
      const buf = editContext.service.getParsedBuffer(editContext.documentId);
      if (!buf) return null;
      const stmt = buf.documentStructure.statements.find(
        (s) => s.id === editContext.statementId,
      );
      if (!stmt) return null;
      const text = buf.parsedDocument.source.slice(
        stmt.range.startOffset,
        stmt.range.endOffset,
      );
      return parseParkedSecurityStatement(text);
    } catch {
      return null;
    }
  }, [parsedOverride, editContext.service, editContext.documentId, editContext.statementId]);

  const mode: 'create' | 'alter' = parsed?.action === 'alter' ? 'alter' : 'create';
  const initialValues: Partial<SecurityFormValues> | undefined = parsed
    ? securityValuesFromParsed(parsed)
    : undefined;

  return (
    <SecurityEditFormBody
      editContext={editContext}
      mode={mode}
      initialValues={initialValues}
      testId="inline-ddl-security-form"
      onCommitted={() => onDone?.()}
      onCancel={() => (onCancel ?? onDone)?.()}
    />
  );
}
